import React, { useEffect, useState, useCallback } from "react";
import apiFetch from "./utils/api";

// ─── helpers ────────────────────────────────────────────────────────────────

function statusColor(status) {
  switch ((status || "").toUpperCase()) {
    case "COMPLETED":
      return "#27ae60";
    case "FAILED":
      return "#e74c3c";
    case "RUNNING":
      return "#2980b9";
    case "STOPPED":
      return "#e67e22";
    default:
      return "#7f8c8d";
  }
}

function statusBg(status) {
  switch ((status || "").toUpperCase()) {
    case "COMPLETED":
      return "#eafaf1";
    case "FAILED":
      return "#fdf3f3";
    case "RUNNING":
      return "#eaf4fb";
    case "STOPPED":
      return "#fef5ec";
    default:
      return "#f4f4f4";
  }
}

function formatElapsed(seconds) {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return `${h}h ${remM}m`;
}

function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

// ─── sub-components ─────────────────────────────────────────────────────────

function StatCard({ label, value, color }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        padding: "14px 20px",
        minWidth: 120,
        textAlign: "center",
        flex: "1 1 120px",
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 700, color: color || "#2c3e50" }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "#7f8c8d", marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        background: statusBg(status),
        color: statusColor(status),
        border: `1px solid ${statusColor(status)}33`,
        letterSpacing: "0.03em",
      }}
    >
      {(status || "UNKNOWN").toUpperCase()}
    </span>
  );
}

function ProgressBar({ value, max, color }) {
  const width = pct(value, max);
  return (
    <div
      style={{
        background: "#e8eaed",
        borderRadius: 4,
        height: 6,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${width}%`,
          background: color || "#27ae60",
          height: "100%",
          transition: "width 0.4s",
        }}
      />
    </div>
  );
}

function JobRow({ job }) {
  const [showLogs, setShowLogs] = useState(false);
  const hasFailed = (job.status || "").toUpperCase() === "FAILED";
  const hasException =
    job.exceptions && job.exceptions !== "NULL" && job.exceptions !== null;

  return (
    <div
      style={{
        borderLeft: `3px solid ${statusColor(job.status)}`,
        background: hasFailed ? "#fff5f5" : "#fafafa",
        borderRadius: "0 6px 6px 0",
        padding: "8px 12px",
        marginBottom: 6,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <StatusBadge status={job.status} />
        <span
          style={{ fontWeight: 500, fontSize: 13, color: "#2c3e50", flex: 1 }}
        >
          {job.jobName}
        </span>
        <span style={{ fontSize: 12, color: "#7f8c8d" }}>
          {job.startTime} → {job.endTime}
        </span>
        <span style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>
          {formatElapsed(job.elapsedTime)}
        </span>
        {job.notes && job.notes !== "NULL" && (
          <span
            style={{
              fontSize: 11,
              color: "#8e44ad",
              background: "#f3eafd",
              padding: "1px 7px",
              borderRadius: 8,
            }}
          >
            {job.notes}
          </span>
        )}
        {(job.logs || hasException) && (
          <button
            onClick={() => setShowLogs((s) => !s)}
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 4,
              border: "1px solid #ccc",
              background: showLogs ? "#e8eaed" : "#fff",
              cursor: "pointer",
              color: "#555",
            }}
          >
            {showLogs ? "Hide logs" : "View logs"}
          </button>
        )}
      </div>
      {showLogs && (
        <div style={{ marginTop: 8 }}>
          {hasException && (
            <pre
              style={{
                background: "#fff0f0",
                border: "1px solid #f5c6cb",
                borderRadius: 4,
                padding: 8,
                fontSize: 11,
                whiteSpace: "pre-wrap",
                color: "#c0392b",
                marginBottom: 6,
              }}
            >
              {job.exceptions}
            </pre>
          )}
          {job.logs && job.logs !== "NULL" && (
            <pre
              style={{
                background: "#1e1e2e",
                borderRadius: 4,
                padding: 10,
                fontSize: 11,
                whiteSpace: "pre-wrap",
                color: "#cdd6f4",
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              {job.logs}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function BatchSection({ batch }) {
  const [expanded, setExpanded] = useState(false);
  const allOk = batch.failed === 0;
  const successRate = pct(batch.completed, batch.totalJobs);

  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        marginBottom: 10,
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setExpanded((s) => !s)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 14px",
          background: allOk ? "#f0faf4" : "#fff8f0",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 14, color: "#555" }}>
          {expanded ? "▾" : "▸"}
        </span>
        <span
          style={{ fontWeight: 600, fontSize: 13, color: "#2c3e50", flex: 1 }}
        >
          {batch.batchName}
        </span>
        <span style={{ fontSize: 12, color: "#7f8c8d" }}>
          {batch.totalJobs} job{batch.totalJobs !== 1 ? "s" : ""}
        </span>
        {batch.failed > 0 && (
          <span style={{ fontSize: 12, fontWeight: 600, color: "#e74c3c" }}>
            {batch.failed} failed
          </span>
        )}
        <span style={{ fontSize: 12, color: "#27ae60", fontWeight: 600 }}>
          {successRate}% OK
        </span>
        <span style={{ fontSize: 12, color: "#555" }}>
          {formatElapsed(batch.totalElapsed)} total
        </span>
        <div style={{ width: 80 }}>
          <ProgressBar
            value={batch.completed}
            max={batch.totalJobs}
            color={allOk ? "#27ae60" : "#e74c3c"}
          />
        </div>
      </div>
      {expanded && (
        <div style={{ padding: "10px 14px", background: "#fff" }}>
          {batch.jobs.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ client }) {
  const [expanded, setExpanded] = useState(false);
  const overallOk = client.failed === 0;
  const successRate = pct(client.completed, client.totalJobs);

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${overallOk ? "#b2dfdb" : "#f5c6cb"}`,
        borderRadius: 10,
        marginBottom: 24,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Client header */}
      <div
        onClick={() => setExpanded((s) => !s)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 20px",
          background: overallOk ? "#e8f8f5" : "#fdf2f0",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: overallOk ? "#27ae60" : "#e74c3c",
            flexShrink: 0,
          }}
        />
        <span
          style={{ fontSize: 16, fontWeight: 700, color: "#1a252f", flex: 1 }}
        >
          {client.client}
        </span>
        <span style={{ fontSize: 13, color: "#7f8c8d" }}>
          {client.totalJobs} jobs
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: overallOk ? "#27ae60" : "#e74c3c",
          }}
        >
          {successRate}% success
        </span>
        <span style={{ fontSize: 13, color: "#555" }}>
          {formatElapsed(client.totalElapsed)} total elapsed
        </span>
        <span style={{ fontSize: 14, color: "#888" }}>
          {expanded ? "▾" : "▸"}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: "16px 20px" }}>
          {/* Summary stat cards */}
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 20,
            }}
          >
            <StatCard label="Total Jobs" value={client.totalJobs} />
            <StatCard
              label="Completed"
              value={client.completed}
              color="#27ae60"
            />
            <StatCard
              label="Failed"
              value={client.failed}
              color={client.failed > 0 ? "#e74c3c" : "#27ae60"}
            />
            <StatCard
              label="Batches"
              value={client.batches.length}
              color="#2980b9"
            />
            <StatCard
              label="Total Elapsed"
              value={formatElapsed(client.totalElapsed)}
              color="#8e44ad"
            />
          </div>

          {/* Overall progress bar */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: "#7f8c8d",
                marginBottom: 4,
              }}
            >
              <span>Overall completion</span>
              <span>
                {client.completed}/{client.totalJobs}
              </span>
            </div>
            <ProgressBar
              value={client.completed}
              max={client.totalJobs}
              color={overallOk ? "#27ae60" : "#e74c3c"}
            />
          </div>

          {/* Batch sections */}
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                marginBottom: 8,
              }}
            >
              Batch Groups
            </div>
            {client.batches.map((batch) => (
              <BatchSection key={batch.batchName} batch={batch} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function BitaDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [clientFilter, setClientFilter] = useState("");

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const url = clientFilter
          ? `/api/bita/batch-jobs?client=${encodeURIComponent(clientFilter)}`
          : "/api/bita/batch-jobs";
        const result = await apiFetch(url);
        if (result) setData(result);
      } catch (e) {
        setError(
          e && e.message ? e.message : "Failed to load BITA batch job data.",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [clientFilter],
  );

  useEffect(() => {
    load();
  }, [load]);

  const clients = data && data.clients ? data.clients : [];
  const totalFailed = clients.reduce((s, c) => s + c.failed, 0);
  const totalJobs = clients.reduce((s, c) => s + c.totalJobs, 0);
  const totalCompleted = clients.reduce((s, c) => s + c.completed, 0);

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Page header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "#1a252f", fontSize: 22 }}>
            BITA Dashboard
          </h2>
          <p style={{ margin: "4px 0 0", color: "#7f8c8d", fontSize: 13 }}>
            Overnight batch job monitoring — BITA Reporting DB
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            type="text"
            placeholder="Filter by client…"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") load();
            }}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: "1px solid #ccc",
              fontSize: 13,
              width: 200,
            }}
          />
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              background: "#2980b9",
              color: "#fff",
              border: "none",
              cursor: refreshing ? "not-allowed" : "pointer",
              fontSize: 13,
              opacity: refreshing ? 0.7 : 1,
            }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Top-level summary strip */}
      {!loading && !error && clients.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 28,
          }}
        >
          <StatCard label="Clients" value={clients.length} color="#2980b9" />
          <StatCard label="Total Jobs" value={totalJobs} />
          <StatCard label="Completed" value={totalCompleted} color="#27ae60" />
          <StatCard
            label="Failed"
            value={totalFailed}
            color={totalFailed > 0 ? "#e74c3c" : "#27ae60"}
          />
          <StatCard
            label="Overall"
            value={`${pct(totalCompleted, totalJobs)}%`}
            color={totalFailed === 0 ? "#27ae60" : "#e74c3c"}
          />
        </div>
      )}

      {/* States */}
      {loading && (
        <div style={{ textAlign: "center", padding: 60, color: "#7f8c8d" }}>
          Loading batch job data…
        </div>
      )}

      {error && (
        <div
          style={{
            background: "#fdf3f3",
            border: "1px solid #f5c6cb",
            borderRadius: 8,
            padding: "16px 20px",
            color: "#c0392b",
            fontSize: 14,
            marginBottom: 20,
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && clients.length === 0 && (
        <div style={{ textAlign: "center", padding: 60, color: "#7f8c8d" }}>
          No batch job records found.
        </div>
      )}

      {/* Client cards */}
      {!loading &&
        clients.map((client) => (
          <ClientCard key={client.client} client={client} />
        ))}
    </div>
  );
}
